import { subscribe } from 'valtio';
import type { StampObject as MapStampObject } from '../../../../common/types';
import * as ImageCache from '../../ImageCache';
import BBoxObject from '../bbox/BBoxObject';
import CanvasObject from '../CanvasObject';
import { opacityWhileDragging } from '../common';
import * as geometry from '../common/geometry';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import { getStampImageUrl } from './common';

export default class EditStampObject extends CanvasObject {
  stamp: MapStampObject;
  _onImageUpdated: (p: LngLat[]) => void;
  imageCanvas: CanvasImageSource | null = null;

  box: Point[];
  refBox: Point[];
  vertices: LngLat[] = [];

  bbox: BBoxObject;

  isDragging: boolean = false;

  constructor(id: string, surface: SurfaceBase, stamp: MapStampObject, onStampUpdated: (p: LngLat[]) => void) {
    super(id, surface);

    this.stamp = stamp;
    this.interactive = true;
    this._onImageUpdated = onStampUpdated;

    this.cleanupFns.push(
      subscribe(this.getStore(), () => {
        this.renderAll();
      }),
    );

    this.refBox = this.box = stamp.geometry.coordinates[0].slice(0, -1).map(([lng, lat]) => {
      this.vertices.push({ lng, lat });
      return this.surface.toScreenCoords({ lng, lat });
    });

    this.bbox = new BBoxObject(`bbox-${id}`, surface, this);
    this.addChild(this.bbox);

    if (stamp.properties.subtype !== 'emoji') {
      ImageCache.getImage(getStampImageUrl(stamp.properties.path)).then((imageCanvas) => {
        this.imageCanvas = imageCanvas;
        this.renderAll();
      });
    }

    this.on('scaleStart', () => {
      this.box = this.bbox.getBBoxCoords();
    });

    this.on('scaling', () => {
      this.box = this.bbox.getBBoxCoords();
      this.renderAll();
    });

    this.on('scaleEnd', () => {
      this.box = this.bbox.getBBoxCoords();
      this.refBox = this.box;
      this.onImageUpdated();
      this.renderAll();
    });

    this.on('rotateStart', () => {
      this.refBox = this.box;
    });

    this.on('rotating', (e) => {
      this.box = geometry.polygonRotate(this.refBox, e.angle, geometry.polygonCentroid(this.refBox));

      this.renderAll();
    });

    this.on('rotateEnd', () => {
      this.onImageUpdated();
    });

    this.on('dragStart', (e) => {
      this.isDragging = true;
      this.refBox = this.box;
      this.bbox.emit('dragStart', e);
    });

    this.on('dragging', (e) => {
      const { x, y } = e.diff();
      this.box = this.refBox.map((c) => ({
        x: c.x + x,
        y: c.y + y,
      }));
      this.bbox.emit('dragging', e);
      this.renderAll();
    });
    this.on('dragEnd', (e) => {
      this.isDragging = false;
      this.bbox.emit('dragEnd', e);
      this.onImageUpdated();
    });
  }

  onImageUpdated = () => {
    const coords = this.box.map((p) => this.surface.toMapCoords(p));
    this.vertices = coords;
    this._onImageUpdated([...coords, coords[0]]);
  };

  getBBoxCoords = (): Point[] => {
    return this.box;
  };

  getCenter = (): Point => {
    return geometry.polygonCentroid(this.box);
  };

  refreshScreenCoords(): void {
    super.refreshScreenCoords();
    this.box = this.vertices.map((v) => this.surface.toScreenCoords(v));
  }

  getCurrentStampObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'stamp') return;
    return currentObject;
  }

  render(ctx: CanvasRenderingContext2D) {
    if (!this.stamp) return;

    const stamp = this.getCurrentStampObject();
    if (!stamp) return;
    const { properties } = stamp;
    const [p1, p2, p3] = this.box;
    const center = this.getCenter();
    const width = geometry.distance(p1, p2);
    const height = geometry.distance(p2, p3);
    const angle = geometry.lineAngle(p1, p2);

    if (properties.subtype === 'emoji' && properties.emoji !== undefined) {
      const fontSize = height;
      ctx.save();
      ctx.font = `${fontSize}px Arial`;
      const dims = ctx.measureText(properties.emoji);
      // remove one pixel because of default canvas text bbox padding
      const offset = dims.fontBoundingBoxDescent / 2 - 1;

      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.globalAlpha = properties.fillOpacity;

      if (this.isDragging && ctx.globalAlpha > opacityWhileDragging) {
        ctx.globalAlpha = opacityWhileDragging;
      }

      ctx.translate(center.x, center.y);
      ctx.rotate(angle);
      ctx.translate(-center.x, -center.y);
      ctx.fillText(properties.emoji, center.x, center.y + offset);
      ctx.restore();
      return;
    }

    if (this.imageCanvas) {
      ctx.save();
      ctx.translate(center.x, center.y);
      ctx.rotate(angle);
      ctx.translate(-center.x, -center.y);

      ctx.globalAlpha = properties.fillOpacity;

      if (this.isDragging && ctx.globalAlpha > opacityWhileDragging) {
        ctx.globalAlpha = opacityWhileDragging;
      }

      ctx.drawImage(this.imageCanvas, center.x - width / 2, center.y - height / 2, width, height);
      ctx.globalAlpha = 1;

      this.surface.resetCanvasTransform(ctx);
    }
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    ctx.beginPath();
    ctx.moveTo(this.box[0].x, this.box[0].y);
    ctx.lineTo(this.box[1].x, this.box[1].y);
    ctx.lineTo(this.box[2].x, this.box[2].y);
    ctx.lineTo(this.box[3].x, this.box[3].y);
    ctx.fillStyle = color;
    ctx.fill();
  }
}
